'use client';
/**
 * MoMent → BoxyLab : l'envoi du CR validé dans la demande BoxyLab (21/10/2026).
 *
 * Deux pièces, posées sur la fiche du dossier MoMent :
 *  · `BandeauBoxyLab` — EN HAUT, quelle que soit la phase du dossier : chaque CR
 *    déposé dans BoxyLab qui n'est plus en vigueur (dévalidé, rectifié, annulé,
 *    envoi en échec après dépôt) est « À RETIRER » à la main de la demande, en
 *    rouge, jusqu'à ce qu'un biologiste confirme le retrait ;
 *  · `EnvoiBoxyLab` — sous le compte rendu : le bouton, la fenêtre de confirmation
 *    côte à côte MoMent | BoxyLab, le déroulé des étapes, l'historique des envois.
 *
 * La page orchestre, l'extension « Nafis Bridge » parle à BoxyLab, le moteur tient
 * le registre et refuse toute étape hors d'ordre. Rien ne part sans clic, rien ne
 * part si une ligne d'identité est rouge — et l'extension refait le contrôle.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { fetchEcriture } from '@/lib/ecrire';
import { fmtDateHeure } from '@/lib/meta';
import { comparer, motifBlocage, toutConforme, type Identite, type LigneControle } from '@/lib/boxylab-identite';
import { trouverExtension, Pont, pdfEnBase64 } from '@/lib/nafis-bridge';
import { avisVersion } from '@/lib/nafis-bridge-protocole';
import type { Progres, ReponseEtat, ReponseLire, ReponseOuvrir, ReponseRetrait } from '@/lib/nafis-bridge-protocole';
import { I, card, Modale, Pastille, useConfirmation } from '@/components/ui';
import { Bouton } from '@/shared/Bouton';
import { PiedActions } from '@/shared/PiedActions';
import { Tableau } from '@/shared/Tableau';

const BASE = '/api/ged/ged/moment';
/**
 * PASSERELLE SUSPENDUE (21/10/2026, 22 h) — premiers dépôts réels : le document déposé disparaît de la demande
 * après « imprimer ». `document/save?id=` ne prend probablement PAS le numéro du document (7 chiffres au
 * relevé, comme l'id d'une demande ; les documents en ont 8) : le CR a pu être rattaché à une AUTRE demande.
 * Aucun envoi, aucune vérification de retrait (elle conclurait « retiré » à tort) tant que ce n'est pas élucidé.
 */
/* Levée le 21/10/2026 au soir par Ines : cause confirmée (code BoxyLab : updateDoc(e, nDemande.id)), corrigée en 0.1.6 ;
   les deux PDF de test sont orphelins (demandes 12474210 / 12474215 inexistantes, 404). Pour suspendre : y remettre un motif. */
const SUSPENDUE: string | null = null;
/** Après un geste, les deux pièces se relisent. */
const MAJ = 'nafis-bridge:maj';

type EtapeFaite = { etape: string; libelle: string; ok: boolean; le: string; detail?: string };
type Envoi = {
  documentId: string; numero: string; crVersion: number; edition: 'standard' | 'confrere'; nomFichier: string; sha256Nafis: string;
  statut: 'en_cours' | 'reussi' | 'echec' | 'abandonne'; etapes: EtapeFaite[]; etapeEchec: string | null; erreur: string | null;
  demandeId: string | null; documentIdBoxy: string | null; creeLe: string; creeParNom: string; retireLe: string | null; retireParNom: string | null;
  retraitVerifie: boolean | null; aRetirer: boolean; interrompu: boolean;
};
type Etat = { envoyable: { ok: boolean; motif: string | null }; edition: 'standard' | 'confrere'; crVersion: number | null; nomFichier: string | null; peutConfirmerRetrait: boolean; envois: Envoi[] };
type Ouverture = { envoi: string; code: string; crVersion: number; edition: 'standard' | 'confrere'; nomFichier: string; sha256: string; identite: Identite; pdf: string };

const LIB_EDITION = { standard: 'Standard', confrere: 'Sans en-tête' } as const;
const encart: React.CSSProperties = { borderInlineStart: '3px solid', borderRadius: 'var(--radius-sm)', padding: '9px 13px', fontSize: 12.5, lineHeight: 1.5 };
const encartRouge: React.CSSProperties = { ...encart, background: 'var(--red-50)', borderInlineStartColor: 'var(--red-600)', color: 'var(--text-body)' };
const encartAmbre: React.CSSProperties = { ...encart, background: 'var(--amber-50)', borderInlineStartColor: 'var(--amber-500)', color: 'var(--text-body)' };
const encartVert: React.CSSProperties = { ...encart, background: 'var(--green-50)', borderInlineStartColor: 'var(--green-600)', color: 'var(--text-body)' };

async function lireEtat(dossier: string): Promise<Etat> {
  const r = await fetch(`${BASE}/dossiers/${dossier}/boxylab`, { credentials: 'include', cache: 'no-store' });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json();
}

async function poster<T>(chemin: string, corps: unknown): Promise<T> {
  const r = await fetchEcriture(`${BASE}/${chemin}`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(corps) });
  const j = await r.json().catch(() => null);
  if (!r.ok) throw new Error(j?.error?.message || `HTTP ${r.status}`);
  return j as T;
}

/** Le compte rendu d'une étape au registre. Un refus (« envoi terminé ») après un échec déjà consigné par le moteur est sans suite. */
const rendreCompte = (envoi: string, corps: Record<string, unknown>) => poster<{ statut: string; erreur: string | null }>(`boxylab/${envoi}/etape`, corps);

function useEtat(dossier: string) {
  const [etat, setEtat] = useState<Etat | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const recharger = useCallback(() => {
    lireEtat(dossier).then((e) => { setEtat(e); setErreur(null); }, (e) => setErreur(e instanceof Error ? e.message : String(e)));
  }, [dossier]);
  useEffect(() => {
    recharger();
    const f = () => recharger();
    window.addEventListener(MAJ, f);
    return () => window.removeEventListener(MAJ, f);
  }, [recharger]);
  return { etat, erreur, recharger };
}
const signalerMaj = () => window.dispatchEvent(new Event(MAJ));

/* ───────────────────────── Le bandeau « à retirer » ───────────────────────── */

export function BandeauBoxyLab({ dossier, cle }: { dossier: string; cle?: string }) {
  const { etat, recharger } = useEtat(dossier);
  const { demander, confirmation } = useConfirmation();
  const [occupe, setOccupe] = useState<string | null>(null);
  const [message, setMessage] = useState<Record<string, string>>({});
  useEffect(() => { recharger(); }, [cle, recharger]);
  const aRetirer = (etat?.envois || []).filter((e) => e.aRetirer);
  if (!aRetirer.length) return null;

  const verifier = async (e: Envoi) => {
    setOccupe(e.documentId);
    setMessage((m) => ({ ...m, [e.documentId]: '' }));
    try {
      const ext = await trouverExtension();
      if (!ext) throw new Error('Extension Nafis Bridge introuvable dans ce navigateur.');
      const pont = new Pont(ext);
      try {
        const r = await pont.demander<ReponseRetrait>({ type: 'verifierRetrait', code: e.numero, documentIdBoxy: Number(e.documentIdBoxy), sha256: e.sha256Nafis });
        if (!r.retire) { setMessage((m) => ({ ...m, [e.documentId]: `Le document n° ${e.documentIdBoxy} est ENCORE dans la demande BoxyLab. Retirez-le, puis vérifiez à nouveau.` })); return; }
        await poster(`boxylab/${e.documentId}/retrait`, { verifie: true });
        signalerMaj();
      } finally { pont.fermer(); }
    } catch (err) {
      setMessage((m) => ({ ...m, [e.documentId]: err instanceof Error ? err.message : String(err) }));
    } finally { setOccupe(null); }
  };
  const declarer = async (e: Envoi) => {
    const ok = await demander(`Vous confirmez avoir retiré le CR v${e.crVersion} (document n° ${e.documentIdBoxy}) de la demande ${e.numero} dans BoxyLab ? Sans relecture par l’extension, la confirmation est tracée comme « déclarée ».`, { confirmer: 'Confirmer le retrait' });
    if (!ok) return;
    setOccupe(e.documentId);
    try { await poster(`boxylab/${e.documentId}/retrait`, { verifie: false }); signalerMaj(); }
    catch (err) { setMessage((m) => ({ ...m, [e.documentId]: err instanceof Error ? err.message : String(err) })); }
    finally { setOccupe(null); }
  };

  return (
    <>
      {aRetirer.map((e) => (
        <div key={e.documentId} role="alert" style={{ ...encartRouge, display: 'grid', gap: 6 }}>
          <div>
            <strong>CR v{e.crVersion} déposé dans BoxyLab le {fmtDateHeure(e.creeLe)} (document n° {e.documentIdBoxy}), à retirer manuellement de la demande {e.numero}.</strong>
            {' '}{e.statut === 'echec' ? `L’envoi a échoué après le dépôt : ${e.erreur || ''}` : e.statut === 'en_cours' ? 'L’envoi s’est arrêté après le dépôt, sans relecture conforme : vérifiez le document dans BoxyLab.' : 'Ce CR n’est plus la version en vigueur du dossier.'}
            {' '}Tout nouvel envoi de ce dossier est bloqué jusqu’à la confirmation du retrait.
          </div>
          {message[e.documentId] && <div style={{ color: 'var(--red-600)', fontWeight: 600 }}>{message[e.documentId]}</div>}
          {SUSPENDUE && <div style={{ fontWeight: 600 }}>{SUSPENDUE} Ne confirmez pas le retrait : le document est peut-être dans une autre demande.</div>}
          {!SUSPENDUE && etat?.peutConfirmerRetrait && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Bouton taille="sm" variante="contour" chargement={occupe === e.documentId} disabled={!!occupe} onClick={() => void verifier(e)}>Vérifier le retrait dans BoxyLab</Bouton>
              <Bouton taille="sm" variante="discret" disabled={!!occupe} onClick={() => void declarer(e)}>Confirmer le retrait sans relecture</Bouton>
            </div>
          )}
        </div>
      ))}
      {confirmation}
    </>
  );
}

/* ───────────────────────── L'envoi et l'historique ───────────────────────── */

type Deroule = { libelle: string; etat: 'attente' | 'ok' | 'echec' }[];
const ETAPES_DEPOT: { cle: 'upload' | 'impression' | 'verification'; libelle: string }[] = [
  { cle: 'upload', libelle: 'Dépôt du PDF' }, { cle: 'impression', libelle: 'Case « imprimer »' }, { cle: 'verification', libelle: 'Relecture de la demande' },
];

type Session =
  | { phase: 'preparation'; message: string }
  | { phase: 'bloque'; ouverture: Ouverture; demandeId: number; lignes: LigneControle[]; motif: string; note?: string | null }
  | { phase: 'confirmation'; ouverture: Ouverture; demandeId: number; lignes: LigneControle[]; doublon: boolean; pont: Pont; doublonConfirme: boolean }
  | { phase: 'depot'; ouverture: Ouverture; deroule: Deroule }
  | { phase: 'termine'; ouverture: Ouverture; deroule: Deroule; ok: boolean; message: string; note: string | null };

/** Ce que l'écran dit de l'ouverture de la demande dans BoxyLab (Ines, 21/10/2026). */
const noteOuverture = (r: ReponseOuvrir | null | undefined, code: string): string | null =>
  !r ? null : r.ouvert ? `La demande ${code} est ouverte dans l’onglet BoxyLab.` : `${r.raison || 'La demande n’a pas été ouverte.'} Ouvrez la demande ${code} dans BoxyLab.`;

export function EnvoiBoxyLab({ dossier, cle }: { dossier: string; cle?: string }) {
  const { etat, erreur, recharger } = useEtat(dossier);
  const [session, setSession] = useState<Session | null>(null);
  const [alerte, setAlerte] = useState<string | null>(null);
  /* La version de l'extension installée, lue une fois : un poste resté sur une ancienne version le voit tout de suite. */
  const [versionExt, setVersionExt] = useState<ReturnType<typeof avisVersion> | null>(null);
  useEffect(() => { recharger(); }, [cle, recharger]);
  useEffect(() => { let vivant = true; void trouverExtension().then((x) => { if (vivant && x) setVersionExt(avisVersion(x.version)); }); return () => { vivant = false; }; }, []);

  const fermer = () => {
    if (session?.phase === 'confirmation') session.pont.fermer();
    setSession(null);
    signalerMaj();
  };

  /** 1-2. Ouvrir l'envoi, lire la demande, comparer. */
  const commencer = async () => {
    setAlerte(null);
    setSession({ phase: 'preparation', message: 'Recherche de l’extension Nafis Bridge…' });
    const ext = await trouverExtension();
    if (!ext) { setSession(null); setAlerte('L’extension Nafis Bridge n’est pas installée dans ce navigateur (Chrome). Demandez-la à la RQ.'); return; }
    const avis = avisVersion(ext.version);
    if (avis.niveau === 'refus') { setSession(null); setAlerte(avis.message); return; }
    let pont: Pont;
    try { pont = new Pont(ext); } catch (e) { setSession(null); setAlerte(e instanceof Error ? e.message : String(e)); return; }
    let garderPont = false;
    try {
      const e = await pont.demander<ReponseEtat>({ type: 'etat' });
      if (e.boxylab !== 'pret') { setSession(null); setAlerte(e.message || 'BoxyLab n’est pas prêt.'); return; }
      setSession({ phase: 'preparation', message: 'Lecture de la demande dans BoxyLab…' });
      const o = await poster<Ouverture>(`dossiers/${dossier}/boxylab/ouvrir`, { versionExtension: ext.version });
      let lu: ReponseLire;
      try {
        lu = await pont.demander<ReponseLire>({ type: 'lire', code: o.code, nomFichier: o.nomFichier, sha256: o.sha256 });
      } catch (err) {
        const m = err instanceof Error ? err.message : String(err);
        await rendreCompte(o.envoi, { etape: 'recherche', ok: false, detail: m }).catch(() => null);
        setSession(null); setAlerte(m); return;
      }
      await rendreCompte(o.envoi, { etape: 'recherche', ok: true, demandeId: lu.demande.demandeId, versionBoxyLab: lu.versionBoxyLab });
      const lignes = comparer({ code: o.code, identite: o.identite }, lu.demande, lu.doublon, lu.dejaDepose ?? null);
      const ctl = await rendreCompte(o.envoi, { etape: 'controles', ok: true, controles: lignes.map((l) => ({ ligne: l.ligne, ok: l.ok })), doublon: lu.doublon, dejaDepose: lu.dejaDepose ?? null });
      /* Le moteur fait foi : s'il a clos l'envoi aux contrôles, on ne propose pas une confirmation qui serait refusée. */
      if (ctl.statut !== 'en_cours') { setSession({ phase: 'bloque', ouverture: o, demandeId: lu.demande.demandeId, lignes, motif: ctl.erreur || 'Le registre a refusé les contrôles : envoi bloqué.' }); return; }
      if (!toutConforme(lignes)) { setSession({ phase: 'bloque', ouverture: o, demandeId: lu.demande.demandeId, lignes, motif: motifBlocage(lignes) || '' }); return; }
      garderPont = true;
      setSession({ phase: 'confirmation', ouverture: o, demandeId: lu.demande.demandeId, lignes, doublon: lu.doublon, pont, doublonConfirme: false });
    } catch (err) {
      setSession(null); setAlerte(err instanceof Error ? err.message : String(err));
    } finally {
      if (!garderPont) pont.fermer();
    }
  };

  /** Blocage d'identité : ouvrir la demande dans BoxyLab pour vérifier l'écart (lecture seule). */
  const ouvrirDansBoxyLab = async () => {
    if (session?.phase !== 'bloque') return;
    const s = session;
    const ext = await trouverExtension();
    if (!ext) { setSession({ ...s, note: 'Extension Nafis Bridge introuvable.' }); return; }
    const pont = new Pont(ext);
    try { setSession({ ...s, note: noteOuverture(await pont.demander<ReponseOuvrir>({ type: 'ouvrir', demandeId: s.demandeId }), s.ouverture.code) }); }
    catch (err) { setSession({ ...s, note: err instanceof Error ? err.message : String(err) }); }
    finally { pont.fermer(); }
  };

  const renoncer = async () => {
    if (session?.phase !== 'confirmation') return;
    await rendreCompte(session.ouverture.envoi, { etape: 'confirmation', ok: false, detail: 'Annulé à la confirmation' }).catch(() => null);
    fermer();
  };

  /** 3-6. Confirmer, déposer, cocher « imprimer », relire — chaque étape consignée aussitôt. */
  const envoyer = async () => {
    if (session?.phase !== 'confirmation') return;
    const { ouverture: o, pont, doublon, doublonConfirme, demandeId } = session;
    const deroule: Deroule = ETAPES_DEPOT.map((e) => ({ libelle: e.libelle, etat: 'attente' }));
    const marquer = (i: number, x: 'ok' | 'echec') => { deroule[i] = { ...deroule[i]!, etat: x }; setSession({ phase: 'depot', ouverture: o, deroule: [...deroule] }); };
    const consignees = new Set<string>();
    /* Les comptes rendus partent EN SÉRIE, dans l'ordre des étapes : envoyés en parallèle, le second
       arrivait avant que le premier soit écrit et le moteur le refusait (essai réel du 21/10/2026). */
    let file: Promise<unknown> = Promise.resolve();
    const consigner = (corps: Record<string, unknown>) => { file = file.then(() => rendreCompte(o.envoi, corps)).catch(() => null); return file; };
    try {
      await rendreCompte(o.envoi, { etape: 'confirmation', ok: true, doublonConfirme: doublon ? doublonConfirme : undefined });
      setSession({ phase: 'depot', ouverture: o, deroule: [...deroule] });
      const pdf = await pdfEnBase64(`${BASE}/boxylab/${o.envoi}/pdf`);
      const resultat = await pont.demander<{ documentIdBoxy: number; ouverture?: ReponseOuvrir }>({ type: 'deposer', code: o.code, identite: o.identite, nomFichier: o.nomFichier, sha256: o.sha256, pdf, doublonConfirme }, (p: Progres) => {
        consignees.add(p.etape);
        marquer(ETAPES_DEPOT.findIndex((e) => e.cle === p.etape), p.etape === 'verification' && !(p.present && p.impression && p.hashIdentique) ? 'echec' : 'ok');
        void consigner({ ...p });
      });
      /* On attend le dernier compte rendu, puis on relit le registre, qui fait foi. */
      await file;
      const e = (await lireEtat(dossier)).envois.find((x) => x.documentId === o.envoi);
      const ok = e?.statut === 'reussi';
      setSession({ phase: 'termine', ouverture: o, deroule: [...deroule], ok, message: ok ? `CR v${o.crVersion} déposé dans BoxyLab et imprimable (document n° ${resultat.documentIdBoxy}).` : (e?.erreur || 'L’envoi n’a pas abouti : voir l’historique.'), note: noteOuverture(resultat.ouverture, o.code) });
    } catch (err) {
      const m = err instanceof Error ? err.message : String(err);
      await file;
      const suivante = ETAPES_DEPOT.find((x) => !consignees.has(x.cle));
      if (suivante) {
        await consigner({ etape: suivante.cle, ok: false, detail: m });
        marquer(ETAPES_DEPOT.indexOf(suivante), 'echec');
      }
      /* Après un échec aussi, la demande s'affiche dans BoxyLab : on y vérifie ce qui s'est passé. */
      const ouv = await pont.demander<ReponseOuvrir>({ type: 'ouvrir', demandeId }).catch(() => null);
      setSession({ phase: 'termine', ouverture: o, deroule: [...deroule], ok: false, message: m, note: noteOuverture(ouv, o.code) });
    } finally {
      pont.fermer();
      signalerMaj();
    }
  };

  if (erreur) return <div style={encartAmbre}>L’état des envois à BoxyLab n’a pas pu être lu ({erreur}). <Bouton taille="sm" variante="discret" onClick={recharger}>Réessayer</Bouton></div>;
  if (!etat) return null;
  const valide = etat.crVersion !== null;
  if (!valide && !etat.envois.length) return null;

  return (
    <section style={{ ...card, padding: 16, display: 'grid', gap: 10 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 14.5, fontWeight: 700, color: 'var(--text-strong)' }}>BoxyLab</h2>
        {etat.nomFichier && <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{etat.nomFichier} · édition {LIB_EDITION[etat.edition]}</span>}
        <span style={{ flex: 1 }} />
        {!SUSPENDUE && etat.envoyable.ok && <Bouton taille="sm" variante="primaire" chargement={session?.phase === 'preparation'} onClick={() => void commencer()}><I n="send" s={12} />Envoyer à BoxyLab</Bouton>}
      </div>
      {SUSPENDUE && <div role="alert" style={encartRouge}>{SUSPENDUE}</div>}
      {!SUSPENDUE && !etat.envoyable.ok && valide && etat.envoyable.motif && <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{etat.envoyable.motif}</div>}
      {session?.phase === 'preparation' && <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{session.message}</div>}
      {alerte && <div role="alert" style={encartAmbre}>{alerte}</div>}
      {!alerte && etat.envoyable.ok && versionExt?.message && <div role="status" style={versionExt.niveau === 'refus' ? encartRouge : encartAmbre}>{versionExt.message}</div>}

      {etat.envois.length > 0 && (
        <Tableau
          id="moment-envois-boxylab"
          niveau="simple"
          ajusterLargeur
          lignes={etat.envois}
          cleLigne={(e) => e.documentId}
          colonnes={[
            { cle: 'le', libelle: 'Date', type: 'date', valeur: (e) => e.creeLe, rendu: (e) => fmtDateHeure(e.creeLe) },
            { cle: 'version', libelle: 'CR', valeur: (e) => e.crVersion, type: 'nombre', rendu: (e) => `v${e.crVersion}` },
            { cle: 'edition', libelle: 'Édition', valeur: (e) => e.edition, type: 'liste', libelleValeur: (v) => LIB_EDITION[v as 'standard'] || v, rendu: (e) => LIB_EDITION[e.edition] },
            { cle: 'par', libelle: 'Par', valeur: (e) => e.creeParNom, rendu: (e) => e.creeParNom },
            { cle: 'statut', libelle: 'Résultat', valeur: (e) => statutEnvoi(e).libelle, rendu: (e) => { const s = statutEnvoi(e); return <Pastille ton={s.ton} compacte>{s.libelle}</Pastille>; } },
            { cle: 'boxy', libelle: 'Demande · document', valeur: (e) => e.documentIdBoxy || '', rendu: (e) => (e.demandeId ? `${e.demandeId}${e.documentIdBoxy ? ` · n° ${e.documentIdBoxy}` : ''}` : '—') },
            { cle: 'detail', libelle: 'Détail', valeur: (e) => detailEnvoi(e), rendu: (e) => detailEnvoi(e) },
          ]}
          vide={{ titre: 'Aucun envoi.' }}
        />
      )}

      {session && session.phase !== 'preparation' && (
        <FenetreEnvoi session={session} onFermer={fermer} onOuvrir={() => void ouvrirDansBoxyLab()} onRenoncer={() => void renoncer()} onEnvoyer={() => void envoyer()}
          onDoublon={(v) => session.phase === 'confirmation' && setSession({ ...session, doublonConfirme: v })} />
      )}
    </section>
  );
}

function statutEnvoi(e: Envoi): { libelle: string; ton: 'success' | 'danger' | 'warning' | 'muted' | 'info' } {
  if (e.retireLe) return { libelle: 'Retiré de BoxyLab', ton: 'muted' };
  if (e.aRetirer) return { libelle: 'À retirer', ton: 'danger' };
  if (e.statut === 'reussi') return { libelle: 'Déposé, imprimable', ton: 'success' };
  if (e.statut === 'echec') return { libelle: 'Échec', ton: 'danger' };
  if (e.statut === 'abandonne') return { libelle: 'Annulé', ton: 'muted' };
  return e.interrompu ? { libelle: 'Interrompu', ton: 'warning' } : { libelle: 'En cours', ton: 'info' };
}

function detailEnvoi(e: Envoi): string {
  if (e.retireLe) return `Retrait confirmé le ${fmtDateHeure(e.retireLe)} par ${e.retireParNom || '—'} (${e.retraitVerifie ? 'vérifié par relecture' : 'déclaré'})`;
  if (e.statut === 'echec') return `${e.etapes.find((x) => !x.ok)?.libelle || 'Échec'} : ${e.erreur || ''}`;
  if (e.statut === 'abandonne') return 'Annulé à la confirmation, rien déposé';
  return e.etapes.length ? `Dernière étape : ${e.etapes[e.etapes.length - 1]!.libelle}` : '';
}

/* ───────────────────────── La fenêtre : confirmer, puis suivre ───────────────────────── */

function FenetreEnvoi({ session, onFermer, onOuvrir, onRenoncer, onEnvoyer, onDoublon }: {
  session: Exclude<Session, { phase: 'preparation' }>; onFermer: () => void; onOuvrir: () => void; onRenoncer: () => void; onEnvoyer: () => void; onDoublon: (v: boolean) => void;
}) {
  const o = session.ouverture;
  const enDepot = session.phase === 'depot';
  const pied = session.phase === 'confirmation'
    ? <PiedActions annuler={{ onClick: onRenoncer }} principale={{ libelle: 'Envoyer à BoxyLab', disabled: session.doublon && !session.doublonConfirme, onClick: onEnvoyer }} />
    : session.phase === 'depot' ? null
      : <PiedActions annuler={{ libelle: 'Fermer', onClick: onFermer }} secondaires={session.phase === 'bloque' ? [{ libelle: 'Ouvrir la demande dans BoxyLab', onClick: onOuvrir }] : []} />;
  return (
    <Modale titre={`Envoyer le CR v${o.crVersion} à BoxyLab`} sousTitre={`${o.nomFichier} · édition ${LIB_EDITION[o.edition]}`} largeur={640}
      onClose={session.phase === 'confirmation' ? onRenoncer : onFermer} protegee={enDepot} onSortieBloquee={() => undefined} sansSaisie={!enDepot} pied={pied}>
      <div style={{ display: 'grid', gap: 12 }}>
        {(session.phase === 'confirmation' || session.phase === 'bloque') && <TableComparaison lignes={session.lignes} />}
        {session.phase === 'bloque' && <div role="alert" style={encartRouge}><strong>{session.motif}</strong> Rien n’a été envoyé.</div>}
        {session.phase === 'confirmation' && session.doublon && (
          <label style={{ ...encartAmbre, display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer' }}>
            <input type="checkbox" checked={session.doublonConfirme} onChange={(e) => onDoublon(e.target.checked)} />
            <span>Un <strong>autre</strong> PDF de ce dossier est déjà dans la demande BoxyLab (même nom de fichier, contenu différent). Je confirme vouloir déposer celui-ci en plus.</span>
          </label>
        )}
        {session.phase === 'confirmation' && <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Le PDF sera déposé dans la demande, puis rendu imprimable. L’extension refait le contrôle d’identité juste avant le dépôt.</div>}
        {(session.phase === 'depot' || session.phase === 'termine') && (
          <ol style={{ margin: 0, paddingInlineStart: 18, display: 'grid', gap: 4, fontSize: 13 }}>
            {session.deroule.map((x) => (
              <li key={x.libelle}>{x.libelle} — {x.etat === 'ok' ? '✅' : x.etat === 'echec' ? '❌' : enDepot ? '…' : 'non faite'}</li>
            ))}
          </ol>
        )}
        {session.phase === 'termine' && <div role="status" style={session.ok ? encartVert : encartRouge}><strong>{session.message}</strong></div>}
        {(session.phase === 'termine' || session.phase === 'bloque') && session.note && <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{session.note}</div>}
      </div>
    </Modale>
  );
}

function TableComparaison({ lignes }: { lignes: LigneControle[] }) {
  return (
    <Tableau
      id="moment-boxylab-comparaison"
      niveau="simple"
      ajusterLargeur
      lignes={lignes}
      cleLigne={(l) => l.ligne}
      colonnes={[
        { cle: 'libelle', libelle: '', valeur: (l) => l.libelle, rendu: (l) => <strong>{l.libelle}</strong> },
        { cle: 'moment', libelle: 'MoMent', valeur: (l) => l.moment, rendu: (l) => l.moment },
        { cle: 'boxylab', libelle: 'BoxyLab', valeur: (l) => l.boxylab, rendu: (l) => l.boxylab },
        { cle: 'ok', libelle: 'Contrôle', valeur: (l) => (l.ok ? 1 : 0), rendu: (l) => (l.ok ? '✅' : l.bloquante ? '❌' : '⚠️') },
      ]}
    />
  );
}

/* ───────────────────────── Le petit avion de la liste des dossiers (Ines, 21/10/2026) ───────────────────────── */

export type EtatListeBoxyLab = { etat: 'a_retirer' | 'depose' | 'en_cours' | 'echec'; crVersion: number; le: string; documentIdBoxy: string | null };
const LIB_LISTE: Record<EtatListeBoxyLab['etat'], string> = {
  depose: 'Déposé dans BoxyLab, imprimable', a_retirer: 'À retirer de BoxyLab', en_cours: 'Envoi BoxyLab en cours', echec: 'Dernier envoi BoxyLab en échec',
};
const COULEUR_LISTE: Record<EtatListeBoxyLab['etat'], string> = {
  depose: 'var(--green-600)', a_retirer: 'var(--red-600)', en_cours: 'var(--cyan-700)', echec: 'var(--amber-600)',
};

/** L'état d'envoi de chaque dossier envoyé (une seule lecture pour toute la liste) ; null tant qu'il n'est pas lu. */
export function useEtatsBoxyLab(): { etats: Record<string, EtatListeBoxyLab> | null; erreur: boolean } {
  const [etats, setEtats] = useState<Record<string, EtatListeBoxyLab> | null>(null);
  const [erreur, setErreur] = useState(false);
  useEffect(() => {
    let vivant = true;
    const lire = () => fetch(`${BASE}/boxylab/etats`, { credentials: 'include', cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((j) => { if (vivant) { setEtats(j); setErreur(false); } }, () => { if (vivant) setErreur(true); });
    void lire();
    window.addEventListener(MAJ, lire);
    return () => { vivant = false; window.removeEventListener(MAJ, lire); };
  }, []);
  return { etats, erreur };
}

/** L'avion en papier, à côté du numéro du dossier, coloré selon l'état ; le détail au survol. Rien quand le dossier n'a jamais été envoyé. */
export function CelluleBoxyLab({ etats, erreur, dossier }: { etats: Record<string, EtatListeBoxyLab> | null; erreur: boolean; dossier: string }) {
  if (erreur) return <span title="L’état des envois à BoxyLab n’a pas pu être lu" style={{ color: 'var(--text-muted)' }}>?</span>;
  const e = etats?.[dossier];
  if (!e) return null;
  const titre = `${LIB_LISTE[e.etat]} — CR v${e.crVersion}${e.documentIdBoxy ? `, document n° ${e.documentIdBoxy}` : ''}, ${fmtDateHeure(e.le)}`;
  return (
    <span title={titre} aria-label={titre} style={{ display: 'inline-flex', color: COULEUR_LISTE[e.etat] }}>
      <I n="send" s={14} />
    </span>
  );
}
